/** @format */

/* eslint @typescript-eslint/prefer-string-starts-ends-with: "warn" */
import {
    ProductConditionStarRating,
    SearchFacet,
} from '@toucan/common/services/search/constants';

import {
    ContentLanguageFallbacks,
    MarketIso,
} from '@toucan/client/global/market';
import { ProductConditionValue } from '@toucan/client/global/product';

export function getContentLanguage(locale: MarketIso): string {
    const isoWithFallbacks = Object.keys(ContentLanguageFallbacks);

    if (isoWithFallbacks.includes(locale)) {
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        return ContentLanguageFallbacks[locale];
    }

    return locale;
}
/**
 * Escape Solr query
 *
 * This escapes the '-' and standard brackets characters so that it is considered literally
 * and not as an _excludes_ or grouped search.
 */

export function escapeSolrQuery(query?: string): string | undefined {
    if (!query) {
        return;
    }
    // '\' is escape character in js, so '\\' is a literal '\' string
    // escape hyphens with double slash
    let result = query.replace(/(^|\W)(-)\b/g, '$1\\$2');

    // escape brackets with double slash
    result = result.replace(/(\(|\))/g, '\\$1');

    return result;
}
/**
 * Transforms filter values into a format Solr can recognise
 *
 * @param key
 * @param value
 */

export function transformToSearchValues(
    key: string,
    value: string | string[] | number | boolean
): string | string[] | number | boolean {
    if ((key as SearchFacet) === SearchFacet.productPriceModifier) {
        if (Array.isArray(value)) {
            return `(${value.join(' AND ')})`;
        }

        return value;
    }

    // Known numerical facets that don't conform to a predictable naming
    // convention
    const knownNumericFacets = ['productPrice'];
    const solrSpecialCharactersRegex =
        /(?=\+|&&|\|{2}|!|\(|\)|{|}|\^|~|\?|\/)/g;

    if (Array.isArray(value)) {
        return value.map((value) =>
            transformToSearchValues(key, value)
        ) as string[];
    }

    // Prepare any numerical facet value
    if (knownNumericFacets.includes(key) || /Num$/.test(key)) {
        const stringValue = String(value);

        if (stringValue.includes('-')) {
            const statement = /-$/.test(stringValue) ? ' TO *' : ' TO ';
            const rangeQuery = stringValue.replace('-', statement);
            return `[${rangeQuery}]`;
        }

        return value;
    }

    // We need to wrap any filter option values in double-quotes so that
    // Solr interprets them as literal values instead of trying to handle
    // special characters
    // https://solr.apache.org/guide/8_4/the-standard-query-parser.html#escaping-special-characters
    if (typeof value === 'string') {
        return value.replace(solrSpecialCharactersRegex, '\\');
    }

    return value;
}
export function getProductConditionStarRating(
    condition: ProductConditionValue
) {
    return {
        [ProductConditionValue.UNKNOWN]: null,
        [ProductConditionValue.FAULTY]: ProductConditionStarRating.ZERO,
        [ProductConditionValue.SR]: ProductConditionStarRating.ZERO,
        [ProductConditionValue['S/R']]: ProductConditionStarRating.ZERO,
        [ProductConditionValue.HEAVILY_USED]: ProductConditionStarRating.ONE,
        [ProductConditionValue['HEAVILY-USED']]: ProductConditionStarRating.ONE,
        [ProductConditionValue.WELL_USED]: ProductConditionStarRating.TWO,
        [ProductConditionValue['WELL-USED']]: ProductConditionStarRating.TWO,
        [ProductConditionValue.GOOD]: ProductConditionStarRating.THREE,
        [ProductConditionValue.EXCELLENT]: ProductConditionStarRating.FOUR,
        [ProductConditionValue.LIKE_NEW]: ProductConditionStarRating.FIVE,
        [ProductConditionValue['LIKE-NEW']]: ProductConditionStarRating.FIVE,
        [ProductConditionValue.NEW]: ProductConditionStarRating.SIX,
    }[condition];
}
